import React from "react";

const CSV_CONFIG = {
    rooms: {
        title: "Importar salas",
        hint: "Formato: estudiante, sala",
        hintDetail:
            "La cabecera es opcional. Cada fila es el nombre del alumno y la sala a la que pertenece.",
        endpoint: "/api/import/rooms",
        previewHeaders: ["Sala", "Alumnos"],
        previewRow: (row) => [row.room, row.students.join(", ")],
        buttonText: (count) => `Importar ${count} sala(s)`,
        resultText: (r) =>
            `${r.name} — ${r.success ? "✓" : "✗"} ${r.success ? `${r.studentsAdded} alumno(s)` : r.error}`,
    },
    students: {
        title: "Importar alumnos a la sala",
        hint: "Formato: un nombre por línea",
        hintDetail:
            "La cabecera es opcional. Cada fila es un estudiante añadido a la sala.",
        endpoint: "/api/import/students",
        previewHeaders: ["Vista previa"],
        previewRow: (name, i) => [`${i + 1}. ${name}`],
        buttonText: (count) => `Añadir ${count} alumno(s)`,
        resultText: (r) => `${r.added} añadido(s), ${r.skipped} ya existían`,
    },
    teachers: {
        title: "Importar profesores desde CSV",
        hint: "Formato: email,nombre,apellidos,contraseña",
        hintDetail:
            "Cabecera obligatoria. La contraseña es opcional. Si no se indica, se genera una temporal.",
        endpoint: "/api/import/teachers",
        previewHeaders: ["Email", "Nombre", "Apellidos"],
        previewRow: (row) => [
            row.email || row.correo,
            row.name || row.nombre || row.displayName,
            row.surname || row.apellido || row.apellidos,
        ],
        buttonText: (count) => `Importar ${count} profesor(es)`,
        resultText: (r) =>
            `${r.email} — ${r.success ? "✓ Creado" : "✗ " + r.error}`,
    },
};

const CsvModal = ({
    type,
    open,
    onClose,
    file,
    preview,
    isImporting,
    results,
    onFileChange,
    onImport,
    onReset,
    extraParams = null,
}) => {
    if (!open) return null;

    const config = CSV_CONFIG[type];
    const isStudents = type === "students";
    const isRooms = type === "rooms";

    const handleImport = async () => {
        await onImport(type, extraParams);
    };

    const handleReset = () => {
        onReset(type);
    };

    const handleClose = () => {
        onClose();
        if (results) handleReset();
    };

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div
                className="modal-container"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: type === "teachers" ? 600 : 560 }}
            >
                <div className="modal-header">
                    <h2>{config.title}</h2>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={isImporting}
                    >
                        ✕
                    </button>
                </div>
                <div className="csv-modal-body">
                    {!results ? (
                        <>
                            <div className="csv-hint">
                                <strong>{config.hint}</strong>
                                {config.hintDetail}
                            </div>

                            <div className="csv-file-input">
                                <input
                                    type="file"
                                    accept=".csv, .xlsx"
                                    onChange={onFileChange}
                                    disabled={isImporting}
                                />
                            </div>

                            {preview.length > 0 && (
                                <>
                                    <h3 style={{ marginBottom: "8px" }}>
                                        Vista previa:
                                    </h3>
                                    {isStudents ? (
                                        <div className="csv-preview-text">
                                            {preview.slice(0, 8).join(", ")}
                                            {preview.length > 8 &&
                                                ` y ${preview.length - 8} más...`}
                                        </div>
                                    ) : (
                                        <table className="csv-preview-table">
                                            <thead>
                                                <tr>
                                                    {config.previewHeaders.map(
                                                        (h, i) => (
                                                            <th key={i}>{h}</th>
                                                        ),
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preview.map((row, i) => (
                                                    <tr key={i}>
                                                        {config
                                                            .previewRow(row, i)
                                                            .map((cell, j) => (
                                                                <td key={j}>
                                                                    {cell}
                                                                </td>
                                                            ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </>
                            )}

                            <button
                                className={
                                    isStudents
                                        ? "btn-add-student"
                                        : "btn-import"
                                }
                                style={isStudents ? {} : { width: "100%" }}
                                onClick={handleImport}
                                disabled={
                                    !file || isImporting || preview.length === 0
                                }
                            >
                                {isImporting ? (
                                    <>
                                        <span className="spinner" />{" "}
                                        Importando...
                                    </>
                                ) : (
                                    config.buttonText(preview.length)
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <h4
                                style={{
                                    marginBottom: "0.75rem",
                                    color: "#2d3748",
                                    fontWeight: 700,
                                }}
                            >
                                Resultados
                            </h4>
                            <div className="csv-results">
                                {Array.isArray(results) ? (
                                    results.map((r, i) => (
                                        <div
                                            key={i}
                                            className={`csv-result-item ${r.status}`}
                                        >
                                            {config.resultText(r)}
                                        </div>
                                    ))
                                ) : (
                                    <div className="csv-result-item success">
                                        {config.resultText(results)}
                                    </div>
                                )}
                            </div>
                            <button
                                className={
                                    isStudents
                                        ? "btn-add-student"
                                        : "btn-import"
                                }
                                style={
                                    isStudents
                                        ? {}
                                        : { width: "100%", marginTop: "1rem" }
                                }
                                onClick={handleReset}
                            >
                                Importar otro archivo
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CsvModal;
